import React from 'react';
import PropTypes from 'prop-types';
import { Box, Container, Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import RevealOnScroll from '../../components/RevealOnScroll';
import AnimatedCounter from '../../components/AnimatedCounter';

/**
 * Full-width navy strip with four live institute stats.
 */
function SocialProofStrip({ totalCount, uniqueCities, uniqueCourses, loading }) {
  const theme = useTheme();
  const navy = theme.palette.brand.navy.dark;
  const orange = theme.palette.brand.orange.main;
  const golden = theme.palette.brand.golden.main;

  const valueStyle = {
    fontWeight: 800,
    background: `linear-gradient(135deg, ${orange}, ${golden})`,
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    fontSize: { xs: '1.75rem', md: '2.5rem' },
    lineHeight: 1.1,
    mb: 0.5,
    display: 'block',
  };

  const labelStyle = {
    color: 'rgba(255,255,255,0.7)',
    letterSpacing: '0.1em',
    fontSize: '0.7rem',
    fontWeight: 600,
  };

  return (
    <Box
      component="section"
      aria-label="Platform statistics"
      sx={{ background: navy, py: { xs: 5, md: 7 } }}
    >
      <Container maxWidth="lg">
        {/* LIVE badge */}
        <RevealOnScroll delay={0}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 1,
              mb: 4,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: orange,
                '@keyframes socialLivePulse': {
                  '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                  '50%': { opacity: 0.4, transform: 'scale(1.5)' },
                },
                animation: 'socialLivePulse 1.6s ease-in-out infinite',
              }}
              aria-hidden="true"
            />
            <Typography
              variant="overline"
              sx={{
                color: orange,
                fontWeight: 800,
                letterSpacing: '0.18em',
                fontSize: '0.7rem',
              }}
            >
              Live now
            </Typography>
          </Box>
        </RevealOnScroll>

        <Grid container spacing={{ xs: 2, md: 4 }} justifyContent="center">
          {/* Institutes listed */}
          <Grid item xs={6} md={3}>
            <RevealOnScroll delay={0}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" component="p" sx={valueStyle}>
                  {loading ? '—' : <AnimatedCounter to={totalCount} suffix="+" />}
                </Typography>
                <Typography variant="overline" sx={labelStyle}>
                  Coaching institutes listed
                </Typography>
              </Box>
            </RevealOnScroll>
          </Grid>

          {/* Cities */}
          <Grid item xs={6} md={3}>
            <RevealOnScroll delay={0.08}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" component="p" sx={valueStyle}>
                  {loading ? '—' : <AnimatedCounter to={uniqueCities} suffix="+" />}
                </Typography>
                <Typography variant="overline" sx={labelStyle}>
                  Cities across India
                </Typography>
              </Box>
            </RevealOnScroll>
          </Grid>

          {/* Courses */}
          <Grid item xs={6} md={3}>
            <RevealOnScroll delay={0.16}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" component="p" sx={valueStyle}>
                  {loading ? '—' : <AnimatedCounter to={uniqueCourses} suffix="+" />}
                </Typography>
                <Typography variant="overline" sx={labelStyle}>
                  Courses covered
                </Typography>
              </Box>
            </RevealOnScroll>
          </Grid>

          {/* 24×7 support — static */}
          <Grid item xs={6} md={3}>
            <RevealOnScroll delay={0.24}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" component="p" sx={{ ...valueStyle }}>
                  24 x 7
                </Typography>
                <Typography variant="overline" sx={labelStyle}>
                  Support on WhatsApp
                </Typography>
              </Box>
            </RevealOnScroll>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

SocialProofStrip.propTypes = {
  totalCount: PropTypes.number,
  uniqueCities: PropTypes.number,
  uniqueCourses: PropTypes.number,
  loading: PropTypes.bool,
};

SocialProofStrip.defaultProps = {
  totalCount: 0,
  uniqueCities: 0,
  uniqueCourses: 0,
  loading: true,
};

export default SocialProofStrip;
